import { z } from 'zod';
import {
  documentCategories,
  documentUploadCategories,
  type DocumentCategory,
  type DocumentKind,
} from '../enums.js';
import { optionalText } from './_fields.js';

/**
 * Contrat réseau des documents du dossier patient. Ce sont des données de santé : le contrat ne
 * laisse jamais sortir la clé de stockage, et le type MIME publié est celui **détecté** par le
 * serveur, pas celui annoncé par le navigateur. La V1 exposait `url: /uploads/<fichier>` et servait
 * ce répertoire à tout utilisateur connecté, tous cabinets confondus (audit sécurité §4.5).
 */

/* -------------------------------------------------------------------------- */
/* Limites et types acceptés — partagés avec le sélecteur de fichier du front   */
/* -------------------------------------------------------------------------- */

/** 25 Mo. Au-delà, 413 `file_too_large`. La V1 s'arrêtait à 10 Mo, une imagerie ne passait pas. */
export const DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

/** 20 fichiers par envoi, au bureau comme depuis le téléphone. */
export const DOCUMENT_MAX_FILES = 20;

/**
 * Dix minutes : le temps de sortir le téléphone et de prendre les photos, pas celui de
 * laisser un lien traîner. Passé ce délai, le jeton est refusé, même s'il n'a pas servi.
 */
export const DOCUMENT_CAPTURE_TTL_MS = 10 * 60 * 1000;

/** Types acceptés, vérifiés sur les octets magiques du fichier reçu. */
export const documentAllowedMimeTypes = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heic-sequence',
  'image/heif',
  'image/heif-sequence',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
export type DocumentMimeType = (typeof documentAllowedMimeTypes)[number];

/**
 * Seuls ces types s'affichent en `inline` (aperçu dans l'application). Tout le reste, DOCX compris,
 * part en `attachment` : un document bureautique rendu par le navigateur est une surface d'attaque.
 */
export const documentInlineMimeTypes = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

export const documentIdParamSchema = z.object({ id: z.uuid() }).strict();

export const documentTokenParamSchema = z.object({ token: z.string().min(16).max(512) }).strict();

export const documentListQuerySchema = z
  .object({
    patientId: z.uuid(),
    encounterId: z.uuid().optional(),
    category: z.enum(documentCategories).optional(),
    /**
     * `deleted=true` liste la **corbeille** : les pièces supprimées en douceur et pas encore
     * purgées, avec leur `deletedAt`. Sans elle, « Restaurer » n'aurait plus rien à quoi
     * s'appliquer dès que l'écran a été rechargé.
     */
    deleted: z
      .enum(['false', 'true'])
      .default('false')
      .transform((v) => v === 'true'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type DocumentListQuery = z.infer<typeof documentListQuerySchema>;

/**
 * Champs texte du téléversement multipart. Volontairement **non `.strict()`** : le même corps porte
 * le champ binaire `file`, lu séparément par la route ; un schéma strict le rejetterait. Toute autre
 * clé inconnue est simplement ignorée, jamais propagée à l'insertion.
 */
export const documentUploadSchema = z.object({
  patientId: z.uuid(),
  encounterId: z.uuid().optional(),
  category: z.enum(documentUploadCategories),
  description: optionalText(2_000).optional(),
});
export type DocumentUploadInput = z.infer<typeof documentUploadSchema>;

/**
 * Émission du lien de capture, par la session du bureau. Le cabinet n'y figure pas : il vient
 * de la session. La catégorie omise reprend le défaut du dépôt classique, « Autre ».
 */
export const documentCaptureIssueSchema = z
  .object({
    patientId: z.uuid(),
    encounterId: z.uuid().optional(),
    category: z.enum(documentUploadCategories).default('other'),
    description: optionalText(2_000).optional(),
    /** Lien affiché jusque-là : révoqué dans la même transaction, s'il est à cette session. */
    replaces: z.uuid().optional(),
  })
  .strict();
export type DocumentCaptureIssueInput = z.infer<typeof documentCaptureIssueSchema>;

export const documentCaptureGrantParamSchema = z.object({ id: z.uuid() }).strict();

/** Le téléphone choisit le rendu. Défaut : garder les JPEG, sans les convertir. */
export const documentCaptureModes = ['photos', 'pdf'] as const;
export type DocumentCaptureMode = (typeof documentCaptureModes)[number];

/**
 * Corps du dépôt téléphone. Pas `.strict()` : les photos voyagent dans le même formulaire,
 * sous le champ `file`, lu à part. Toute autre clé — un cabinet, un patient — est ignorée.
 */
export const documentCaptureRedeemSchema = z.object({
  mode: z.enum(documentCaptureModes).default('photos'),
});
export type DocumentCaptureRedeemInput = z.infer<typeof documentCaptureRedeemSchema>;

export const documentCaptureTokenParamSchema = z
  .object({ token: z.string().min(16).max(512) })
  .strict();

/** Le fichier lui-même ne se remplace pas : on en téléverse un nouveau et on supprime l'ancien. */
export const documentUpdateSchema = z
  .object({
    category: z.enum(documentUploadCategories).optional(),
    description: optionalText(2_000).optional(),
    encounterId: z.uuid().nullable().optional(),
  })
  .strict();
export type DocumentUpdateInput = z.infer<typeof documentUpdateSchema>;

/** `?inline=1` sur le contenu : aperçu au lieu du téléchargement, accordé aux seuls types d'aperçu. */
export const documentContentQuerySchema = z
  .object({
    inline: z
      .enum(['0', '1'])
      .default('0')
      .transform((v) => v === '1'),
  })
  .strict();
export type DocumentContentQuery = z.infer<typeof documentContentQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

/** Métadonnées seules. `storageKey` n'apparaît nulle part : le contenu passe par une route contrôlée. */
export interface DocumentDto {
  id: string;
  patientId: string;
  encounterId: string | null;
  kind: DocumentKind;
  category: DocumentCategory;
  /** Nom d'affichage nettoyé : ni séparateur de chemin, ni caractère de contrôle, 200 caractères. */
  fileName: string;
  /** Type détecté sur les octets magiques, jamais celui annoncé par le client. */
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  description: string | null;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface DocumentListDto {
  items: DocumentDto[];
  page: number;
  pageSize: number;
  total: number;
}

/** URL d'aperçu à durée de vie courte, pour les balises qui ne portent pas d'en-tête d'autorisation. */
export interface DocumentSignedUrlDto {
  url: string;
  expiresAt: string;
}

/**
 * Lien affiché en QR. L'URL est celle du produit ; elle porte le jeton, rien d'autre. `id`
 * désigne la ligne, pour que le bureau suive son état ; il n'ouvre aucun droit d'envoi.
 */
export interface DocumentCaptureLinkDto {
  id: string;
  url: string;
  expiresAt: string;
}

export const documentCaptureGrantStates = ['pending', 'used', 'expired'] as const;
export type DocumentCaptureGrantState = (typeof documentCaptureGrantStates)[number];

/** Ce que le bureau apprend de son QR : servi ou non. Un lien révoqué compte comme servi. */
export interface DocumentCaptureGrantStatusDto {
  status: DocumentCaptureGrantState;
}

/** Ce que le téléphone apprend du lien : sa date de fin. Pas le dossier, pas le patient. */
export interface DocumentCaptureStatusDto {
  expiresAt: string;
}

/**
 * Résultat du dépôt téléphone. Le compte et le type suffisent : l'identifiant du patient et la
 * description ne repartent pas vers l'appareil qui a scanné le code.
 */
export interface DocumentCaptureResultDto {
  count: number;
  mimeType: string;
}
